/**
 * Conference Utilities
 * 
 * Provides functions to work with conference data, including ID to abbreviation mapping
 */

import { conferenceData } from "../data/conferenceData";

/**
 * Legacy URL slugs that should still resolve after conference rebrands.
 * Keys are lowercase path segments; values are current abbreviation slugs.
 */
const CONFERENCE_ABBREVIATION_ALIASES: Record<string, string> = {
  // Display-oriented / interim slugs that should still resolve
  american: "aac",
  "big-south-ovc": "ovc", // Big South-OVC → Ohio Valley Conference
};

/**
 * Maps conference ID to abbreviation
 * @param conferenceId - The conference ID (e.g., "1")
 * @returns The conference abbreviation (e.g., "acc") or null if not found
 */
export const getConferenceAbbreviation = (conferenceId: string): string | null => {
  const conference = (conferenceData.conferences as Record<string, { abbreviation: string }>)[conferenceId];
  return conference?.abbreviation || null;
};

/**
 * Maps conference abbreviation to ID
 * @param abbreviation - The conference abbreviation (e.g., "acc" or "ACC")
 * @returns The conference ID (e.g., "1") or null if not found
 */
export const getConferenceIdFromAbbreviation = (abbreviation: string): string | null => {
  if (!abbreviation) return null;
  const conferences = conferenceData.conferences;
  let lowerAbbreviation = abbreviation.toLowerCase();
  if (CONFERENCE_ABBREVIATION_ALIASES[lowerAbbreviation]) {
    lowerAbbreviation = CONFERENCE_ABBREVIATION_ALIASES[lowerAbbreviation];
  }
  for (const [id, conference] of Object.entries(conferences)) {
    if (conference.abbreviation.toLowerCase() === lowerAbbreviation) {
      return id;
    }
  }
  return null;
};

/**
 * Validates if a string is a valid conference abbreviation
 * @param abbreviation - The abbreviation to validate
 * @returns true if valid, false otherwise
 */
export const isValidConferenceAbbreviation = (abbreviation: string): boolean => {
  return getConferenceIdFromAbbreviation(abbreviation) !== null;
};

/**
 * Gets conference name from abbreviation
 * @param abbreviation - The conference abbreviation (e.g., "sec" or "SEC")
 * @returns The conference name (e.g., "Southeastern Conference") or null if not found
 */
export const getConferenceNameFromAbbreviation = (abbreviation: string): string | null => {
  const conferenceId = getConferenceIdFromAbbreviation(abbreviation);
  if (!conferenceId) return null;
  const conference = (conferenceData.conferences as Record<string, { name?: string }>)[
    conferenceId
  ];
  return conference?.name || null;
};

/**
 * Gets the conference ID for a team
 * @param teamId - The team ID (e.g., "2" for Alabama)
 * @returns The conference ID (e.g., "8" for SEC) or null if not found
 */
export const getConferenceIdForTeam = (teamId: string): string | null => {
  const teams = conferenceData.teams as Record<string, string>;
  return teams[teamId] || null;
};

/**
 * Gets the conference display name for a team
 * @param teamId - The team ID (e.g., "333" for Alabama)
 * @returns Prefer shortName (e.g. "SEC"), then full name, or null
 */
export const getConferenceNameForTeam = (teamId: string): string | null => {
  const conferenceId = getConferenceIdForTeam(teamId);
  if (!conferenceId) return null;
  const conference = (conferenceData.conferences as Record<
    string,
    { name?: string; shortName?: string }
  >)[conferenceId];
  if (!conference) return null;
  return conference.shortName || conference.name || null;
};

